import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  KRÉTA e-ÜGYINTÉZÉS – igazolás-kérelmek átemelése.
 *
 *  A szülő (vagy az orvos az EESZT-en át) a KRÉTA e-Ügyintézésben adja be a hiányzás
 *  igazolását. Ez a képernyő állítja be, hogy a rendszer milyen gyakran hozza át őket,
 *  és mutatja meg, mi jött át – tanulónként, ügyiratszámmal.
 *
 *  Egy fiók kell hozzá: az intézmény ügyintézői KRÉTA-belépése. Nem per-tanár művelet.
 */
@Component({
  selector: 'app-admin-kreta-eugy',
  templateUrl: './kreta-eugy.component.html',
  styleUrls: ['./kreta-eugy.component.scss'],
})
export class KretaEugyComponent implements OnInit, OnDestroy {
  intezmeny = '';
  toltodik = true;
  hiba = '';

  b: any = { enabled: 0, intervallum_perc: 720, csak_eeszt: 0, csak_elfogadott: 1, napok_vissza: 60 };
  jelszo = '';
  totp = '';
  ment = false;

  kerelmek: any[] = [];
  kerelmekFut = false;
  csakEeszt = false;
  csakNemAtvezetett = false;

  /*  A NAPLÓZÁSHOZ MÁR FELVETT KRÉTA-FIÓKOK. Ha az ügyintézői belépés köztük van,
   *  elég kiválasztani – a jelszót nem kell újra begépelni, és nem is másoljuk át:
   *  mindig az eredeti sorból olvassuk. */
  fiokok: any[] = [];

  tesztFut = false;
  tesztEredmeny: { ok: boolean; szoveg: string } | null = null;

  szinkronFut = false;
  allapot: any = null;
  private idozito: any = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.betolt();
    await this.fiokokBetolt();
    await this.kerelmeketBetolt();
  }

  ngOnDestroy() { if (this.idozito) clearInterval(this.idozito); }

  get ut(): string { return encodeURI(this.intezmeny); }

  /** Emberi szöveg a percekből – a „720 perc" senkinek nem mond semmit. */
  get gyakorisagSzoveg(): string {
    const p = Number(this.b?.intervallum_perc) || 0;
    if (!p) return '';
    if (p % 1440 === 0) return p === 1440 ? 'naponta egyszer' : `${p / 1440} naponta`;
    if (p % 60 === 0) return `${p / 60} óránként`;
    return `${p} percenként`;
  }

  async betolt() {
    this.toltodik = true;
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/kreta-eugy/beallitas/${this.ut}`));
      this.b = r || this.b;
      this.allapot = r?.allapot || null;
      if (r?.fut) this.futastKovet();
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A beállítás nem tölthető be.';
    } finally {
      this.toltodik = false;
    }
  }

  async mentes() {
    this.ment = true;
    try {
      await firstValueFrom(this.http.put(`/api/kreta-eugy/beallitas/${this.ut}`, {
        ...this.b,
        intezmeny: this.intezmeny,
        jelszo: this.jelszo || undefined,
        totp: this.totp || undefined,
      }));
      this.jelszo = '';
      this.totp = '';
      this.data.sendToast('Beállítás mentve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.ment = false;
    }
  }

  async szinkron() {
    if (!confirm('Elindítod az átemelést? A rendszer belép a KRÉTÁ-ba, lekéri a kérelmeket, '
      + 'és a döntéssel lezárt igazolásokat átvezeti a naplóra. Percekig tarthat.')) return;
    this.szinkronFut = true;
    try {
      await firstValueFrom(this.http.post(`/api/kreta-eugy/szinkron/${this.ut}`, {}));
      this.data.sendToast('Az átemelés elindult.', 'success');
      this.futastKovet();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az átemelés nem indult el.', 'error');
      this.szinkronFut = false;
    }
  }

  /*  A menet percekig tart, ezért az állapotot kérdezzük – ugyanúgy, ahogy a
   *  frissítésnél: a képernyő mutassa, hol tart, ne csak pörögjön. */
  private futastKovet() {
    this.szinkronFut = true;
    if (this.idozito) clearInterval(this.idozito);
    this.idozito = setInterval(async () => {
      try {
        const r: any = await firstValueFrom(this.http.get(`/api/kreta-eugy/allapot/${this.ut}`));
        this.allapot = r;
        if (!r?.fut) {
          clearInterval(this.idozito);
          this.idozito = null;
          this.szinkronFut = false;
          await this.betolt();
          await this.kerelmeketBetolt();
        }
      } catch (e) { /* épp fut – várunk tovább */ }
    }, 3000);
  }

  async fiokokBetolt() {
    try {
      this.fiokok = await firstValueFrom(this.http.get<any[]>(`/api/kreta-eugy/fiokok/${this.ut}`)) || [];
    } catch (e) {
      this.fiokok = [];
    }
  }

  /** A kiválasztott fiók neve – hogy a képernyő megmondja, kivel fog belépni. */
  get valasztottFiok(): any {
    return this.fiokok.find((f) => f.allomany === this.b?.forras_allomany) || null;
  }

  /*  CSAK TESZT: belép, megnézi a kérelmek listáját, és NEM ír semmit. A teljes menet
   *  percekig tart és a naplóba ír – a beállításkor viszont csak annyi a kérdés, hogy
   *  a fiók bejut-e egyáltalán. */
  async teszt() {
    this.tesztFut = true;
    this.tesztEredmeny = null;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/kreta-eugy/teszt/${this.ut}`, {
        forras_allomany: this.b?.forras_allomany || '',
        kreta_user: this.b?.kreta_user || '',
        kreta_host: this.b?.kreta_host || '',
        jelszo: this.jelszo || undefined,
        totp: this.totp || undefined,
      }));
      this.tesztEredmeny = { ok: !!r?.ok, szoveg: r?.ok ? r.uzenet : (r?.error || 'A teszt nem sikerült.') };
    } catch (e: any) {
      this.tesztEredmeny = { ok: false, szoveg: e?.error?.error || 'A teszt nem futott le.' };
    } finally {
      this.tesztFut = false;
    }
  }

  async kerelmeketBetolt() {
    this.kerelmekFut = true;
    try {
      const p = new URLSearchParams();
      if (this.csakEeszt) p.set('csak_eeszt', '1');
      if (this.csakNemAtvezetett) p.set('csak_nem_atvezetett', '1');
      const q = p.toString() ? `?${p.toString()}` : '';
      this.kerelmek = await firstValueFrom(
        this.http.get<any[]>(`/api/kreta-eugy/kerelmek/${this.ut}${q}`)) || [];
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A kérelmek nem tölthetők be.', 'error');
    } finally {
      this.kerelmekFut = false;
    }
  }

  /*  ÚJRAVEZETÉS KRÉTA-BELÉPÉS NÉLKÜL. A napi valóság: az igazolás hamarabb érkezik,
   *  mint ahogy a tanár a hiányzást bevezeti – ilyenkor nincs mit újra letölteni. */
  async atvezet(mind = false) {
    if (!confirm(mind
      ? 'Minden eltett kérelmet újra átvezetek a naplóra. Folytatod?'
      : 'A még át nem vezetett kérelmeket vezetem át a naplóra. Folytatod?')) return;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/kreta-eugy/atvezet/${this.ut}`, { mind }));
      this.data.sendToast(`${r.atvezetve} kérelem átvezetve, ${r.orak} óra igazolva.`, 'success');
      await this.kerelmeketBetolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az átvezetés nem sikerült.', 'error');
    }
  }
}
